import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Employee, EmployeeFilterResource } from "@/models/employees";
import employeeBreadcrumbStore from "@/stores/employeeBreadcrumbStore";
import { useNavigate } from "react-router-dom";
import { checkPermissionsWithAlert, formatDate } from "@/util";
import { PermissionName } from "@/models/apiModels";
import filtersStore, { EmployeeFilterData } from "@/stores/filtersStore";
import BaseButton from "@/components/common/baseButton";

interface IEmployeeInfoProps {
    selectedEmployee?: Employee | null;
    employeeFilter: EmployeeFilterResource;
    filterTabsArray: Array<{ id: string; name: string; value: string }>;
    hiddenFilter: boolean | false;
}

const EmployeeInfoTab: FC<IEmployeeInfoProps> = (props: IEmployeeInfoProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    function sendEmail(email: string) {
        window.location.href = `mailto:${email}`;
    }

    function validatePermissionAndNavigate(id: number | undefined) {
        if ((checkPermissionsWithAlert(PermissionName.EmployeeGeneralInfo), true)) {
            navigate("/employees/" + id + "/info");
        } else if ((checkPermissionsWithAlert(PermissionName.EmployeeDigitalWallet), true)) {
            navigate("/employees/" + id + "/wallet");
        } else if ((checkPermissionsWithAlert(PermissionName.EmployeeOrders), true)) {
            navigate("/employees/" + id + "/employeeOrders");
        } else if (checkPermissionsWithAlert(PermissionName.EditAttachedDoc)) {
            navigate("/employees/" + id + "/docs");
        } else {
            return;
        }
    }
    function openEmployeePage(id: number | undefined, employeeName: string) {
        if (!id) {
            return;
        }

        const employeeFilterData = new EmployeeFilterData();
        employeeFilterData.filter = props.employeeFilter;
        employeeFilterData.filterTabs = props.filterTabsArray;
        employeeFilterData.hiddenFilter = props.hiddenFilter;

        filtersStore.setEmployeeFilterData(employeeFilterData);
        employeeBreadcrumbStore.update(employeeName);
        validatePermissionAndNavigate(id);
    }

    return (
        <div className="employee-info-tab-left">
            <div className="employee-info-tab-header">
                <div className="employee-info-tab-header-left">
                    {props.selectedEmployee?.firstName} {props.selectedEmployee?.lastName}
                </div>
                <div className="employee-info-tab-header-right">
                    {props.selectedEmployee?.isActive ? (
                        <div className="status-element">
                            <div className="status-color-active"></div>
                            <div style={{ textTransform: "uppercase" }}>{t("employee.ActiveStatus")}</div>
                        </div>
                    ) : (
                        <div className="status-element">
                            <div className="status-color-inactive"></div>
                            <div style={{ textTransform: "uppercase" }}>{t("employee.InactiveStatus")}</div>
                        </div>
                    )}
                </div>
            </div>
            <div className="employee-info-tab-content">
                <div className="employee-info-tab-content-left">
                    <div className="font-style-16">
                        <div>{t("employee.Card.Gender")}</div>
                        <div>{t("employee.Card.Number")}</div>
                        <div>{t("employee.Card.IdCard")}</div>
                        <div>{t("employee.Card.Department")}</div>
                        <div>{t("employee.Card.Group")}</div>
                    </div>
                    <div className="font-style-bold-16">
                        <div style={{ height: "18.75px" }}>
                            {props.selectedEmployee?.gender && t(`gender.${props.selectedEmployee?.gender}`)}
                        </div>
                        <div
                            onClick={() =>
                                openEmployeePage(
                                    props.selectedEmployee?.id,
                                    props.selectedEmployee?.firstName + " " + props.selectedEmployee?.lastName
                                )
                            }
                            style={{ height: "18.75px" }}
                            className="link-element"
                        >
                            {props.selectedEmployee?.employeeNumber}
                        </div>
                        <div style={{ height: "18.75px" }}>{props.selectedEmployee?.employeePID}</div>
                        <div style={{ height: "18.75px" }}>{props.selectedEmployee?.organizationUnit}</div>
                        <div style={{ height: "18.75px" }}>{props.selectedEmployee?.employeeGroup}</div>
                    </div>
                </div>
                <div className="employee-info-tab-content-right">
                    <div className="font-style-16">
                        <div>{t("employee.Card.TerminationDate")}</div>
                        <div>{t("employee.Card.StartDate")}</div>
                        <div>{t("employee.Card.Email")}</div>
                        <div>{t("employee.Card.MobilePhone")}</div>
                        <div>{t("employee.Card.WorkPhone")}</div>
                    </div>
                    <div className="font-style-bold-16">
                        <div style={{ height: "18.75px" }}>
                            {props.selectedEmployee?.workEndDate ? formatDate(props.selectedEmployee?.workEndDate) : ""}
                        </div>
                        <div style={{ height: "18.75px" }}>
                            {props.selectedEmployee?.workStartDate
                                ? formatDate(props.selectedEmployee?.workStartDate)
                                : ""}
                        </div>
                        <div
                            onClick={() => sendEmail(props.selectedEmployee?.email ?? "")}
                            className="link-element"
                            style={{ height: "18.75px" }}
                        >
                            {props.selectedEmployee?.email}
                        </div>
                        <div style={{ height: "18.75px" }}>{props.selectedEmployee?.mobilePhone}</div>
                        <div style={{ height: "18.75px" }}>{props.selectedEmployee?.workPhone}</div>
                    </div>
                </div>
                <div>
                    <BaseButton
                        style={{ width: "100%" }}
                        className="basic-button"
                        onClick={() =>
                            openEmployeePage(
                                props.selectedEmployee?.id,
                                props.selectedEmployee?.firstName + " " + props.selectedEmployee?.lastName
                            )
                        }
                    >
                        {t("employee.Card.MoreInfoBtn")}
                    </BaseButton>
                </div>
            </div>
        </div>
    );
};

export default EmployeeInfoTab;
